import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * useAutoLogout — inactivity-based logout per role policy.
 *
 * Reads `/me/security-policy.auto_logout` (cached on mount). When the
 * user has been idle longer than the policy's `duration_seconds`,
 * forces logout — UNLESS an active call is in progress (per
 * `window.__orb_active_call` flag set by CallContext).
 *
 * Activity sources: mousedown, keydown, touchstart, mousemove (throttled).
 */
export default function useAutoLogout() {
  const { user, api, logout } = useAuth();
  const policyRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef(null);

  const fetchPolicy = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/me/security-policy');
      policyRef.current = res.data;
    } catch (e) { /* non-fatal */ }
  }, [user, api]);

  useEffect(() => { fetchPolicy(); }, [fetchPolicy]);

  // Reset activity stamp on user input.
  useEffect(() => {
    if (!user) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    let lastMove = 0;
    const bumpMove = () => { const t = Date.now(); if (t - lastMove > 500) { lastMove = t; bump(); } };
    const events = ['mousedown', 'keydown', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, bump, true));
    window.addEventListener('mousemove', bumpMove, true);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump, true));
      window.removeEventListener('mousemove', bumpMove, true);
    };
  }, [user]);

  // Tick once per second; force-logout when threshold exceeded and
  // no active call is running.
  useEffect(() => {
    if (!user) return;
    intervalRef.current = setInterval(() => {
      const p = policyRef.current?.auto_logout;
      if (!p?.enabled) return;
      const duration = (p.duration_seconds || 0) * 1000;
      if (!duration) return;
      const idle = Date.now() - lastActivityRef.current;
      if (idle < duration) return;
      // Skip during active calls (Twilio / WebRTC / Teams).
      if (window.__orb_active_call) return;
      try { logout(); } catch (_) {}
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [user, logout]);
}
